import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  UserPlus, 
  Trash2, 
  Edit2, 
  Crown,
  Shield,
  User, 
  Mail, 
  Eye,
  EyeOff,
  Loader2,
  MessageSquare,
  Users,
  Bot,
  Package,
  Puzzle,
  CalendarDays,
  Settings,
  LayoutDashboard,
  Lock,
  ChevronDown,
  Zap,
  Handshake,
  Phone,
  Instagram,
} from "lucide-react";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { invalidateWorkspaceCache } from "@/hooks/useWorkspace";
import { dispatchInboxScopeChanged } from "@/lib/inbox-scope-changed";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useResourceLimits } from "@/hooks/useResourceLimits";
import { useUserRole } from "@/hooks/useUserRole";
import type { MemberPermissions } from "@/hooks/useMemberPermissions";
import type { ConversationsVisibility } from "@/lib/conversation-filters";
import type { AppointmentsCalendarVisibility } from "@/lib/appointment-filters";
import type { DealsVisibility } from "@/lib/deal-filters";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MEMBER_PROFILE_PRESETS,
  MEMBER_PROFILE_LABELS,
  MEMBER_PROFILE_DESCRIPTIONS,
  applyMemberProfile,
  detectMemberProfile,
  type MemberProfile,
} from "@/lib/member-profile-presets";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";
import { useWorkspace } from "@/hooks/useWorkspace";
import { MESSAGING_CHANNEL_GROUPS } from "@/lib/messaging-integration-providers";
import {
  getMessagingIntegrationDetail,
  getMessagingIntegrationLabel,
  groupMessagingIntegrationsByChannel,
  type MessagingIntegrationOption,
} from "@/lib/integration-display-label";


interface Member {
  id: string;
  user_id: string;
  email: string;
  role: string;
  display_name: string | null;
  default_funnel_id?: string | null;
  default_stage_slug?: string | null;
  permissions: MemberPermissions | null;
  invited_at: string | null;
  invited_by: string | null;
  last_active_at: string | null;
  last_sign_in_at: string | null;
}

interface WorkspaceMembersManagerProps {
  workspaceId: string;
}

const PERMISSION_OPTIONS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "conversations", label: "Conversas", icon: MessageSquare },
  { key: "crm", label: "CRM (Leads)", icon: Users },
  { key: "deals", label: "Negociações", icon: Handshake },
  { key: "appointments", label: "Agendamentos", icon: CalendarDays },
  { key: "tasks", label: "Tarefas", icon: CalendarDays },
  { key: "automations", label: "Automações", icon: Zap },
  { key: "agents", label: "Agentes IA", icon: Bot },
  { key: "products", label: "Produtos", icon: Package },
  { key: "integrations", label: "Integrações", icon: Puzzle },
  { key: "settings", label: "Configurações", icon: Settings },
] as const;

const DEFAULT_PERMISSIONS: MemberPermissions = {
  conversations: true,
  crm: true,
  agents: false,
  products: false,
  integrations: false,
  settings: false,
  appointments: true,
  dashboard: true,
  tasks: true,
  automations: false,
  deals: true,
  conversations_visibility: "workspace_inbox",
  appointments_calendar_visibility: "all",
  deals_visibility: "all",
};

const NEW_MEMBER_DEFAULT_PERMISSIONS: MemberPermissions = {
  ...DEFAULT_PERMISSIONS,
  ...MEMBER_PROFILE_PRESETS.comercial,
  member_profile: "comercial",
};

function mergeMemberPermissions(raw: MemberPermissions | null | undefined): MemberPermissions {
  if (!raw) return { ...DEFAULT_PERMISSIONS };
  const vis = raw.conversations_visibility;
  const apptVis = raw.appointments_calendar_visibility;
  const dealsVis = raw.deals_visibility;
  return {
    ...DEFAULT_PERMISSIONS,
    ...raw,
    conversations_visibility:
      vis === "assigned_only" ||
      vis === "workspace_inbox" ||
      vis === "workspace_inbox_all"
        ? vis
        : "workspace_inbox",
    appointments_calendar_visibility:
      apptVis === "assigned_only" || apptVis === "all" ? apptVis : "all",
    deals_visibility:
      dealsVis === "assigned_only" || dealsVis === "all" ? dealsVis : "all",
    deals: raw.deals ?? raw.crm ?? DEFAULT_PERMISSIONS.deals,
  };
}

function MemberProfileField({
  value,
  onChange,
}: {
  value: MemberProfile;
  onChange: (profile: MemberProfile) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border p-3 bg-muted/20">
      <Label className="text-sm">Perfil do membro</Label>
      <Select value={value} onValueChange={(v) => onChange(v as MemberProfile)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(["gestor", "comercial", "suporte", "custom"] as MemberProfile[]).map((p) => (
            <SelectItem key={p} value={p}>
              {MEMBER_PROFILE_LABELS[p]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{MEMBER_PROFILE_DESCRIPTIONS[value]}</p>
    </div>
  );
}

function ConversationsVisibilityField({
  value,
  onChange,
}: {
  value: ConversationsVisibility;
  onChange: (value: ConversationsVisibility) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border p-3 bg-muted/20">
      <Label className="text-sm">Visibilidade em Conversas</Label>
      <p className="text-xs text-muted-foreground">
        Somente o gestor define quais conversas o membro enxerga na inbox.
      </p>
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as ConversationsVisibility)}
        className="gap-2"
      >
        <div className="flex items-start gap-2">
          <RadioGroupItem value="assigned_only" id="conv-vis-assigned" className="mt-0.5" />
          <label htmlFor="conv-vis-assigned" className="text-sm cursor-pointer leading-snug">
            Apenas atribuídas a este membro
          </label>
        </div>
        <div className="flex items-start gap-2">
          <RadioGroupItem value="workspace_inbox" id="conv-vis-inbox" className="mt-0.5" />
          <label htmlFor="conv-vis-inbox" className="text-sm cursor-pointer leading-snug">
            Inbox do workspace (sem dono + atribuídas a ele). Não vê contatos atribuídos a outros membros, mesmo no mesmo funil.
          </label>
        </div>
        <div className="flex items-start gap-2">
          <RadioGroupItem value="workspace_inbox_all" id="conv-vis-inbox-all" className="mt-0.5" />
          <label htmlFor="conv-vis-inbox-all" className="text-sm cursor-pointer leading-snug">
            Inbox do workspace (sem dono + atribuídas a qualquer membro)
          </label>
        </div>
      </RadioGroup>
    </div>
  );
}

function AppointmentsCalendarVisibilityField({
  value,
  onChange,
}: {
  value: AppointmentsCalendarVisibility;
  onChange: (value: AppointmentsCalendarVisibility) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border p-3 bg-muted/20">
      <Label className="text-sm">Visibilidade em Agendamentos</Label>
      <p className="text-xs text-muted-foreground">
        Define quais agendamentos o membro enxerga nas agendas às quais tem acesso.
      </p>
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as AppointmentsCalendarVisibility)}
        className="gap-2"
      >
        <div className="flex items-start gap-2">
          <RadioGroupItem value="all" id="appt-vis-all" className="mt-0.5" />
          <label htmlFor="appt-vis-all" className="text-sm cursor-pointer leading-snug">
            Todos os agendamentos das agendas permitidas
          </label>
        </div>
        <div className="flex items-start gap-2">
          <RadioGroupItem value="assigned_only" id="appt-vis-assigned" className="mt-0.5" />
          <label htmlFor="appt-vis-assigned" className="text-sm cursor-pointer leading-snug">
            Apenas agendamentos de leads atribuídos a este membro
          </label>
        </div>
      </RadioGroup>
    </div>
  );
}

function DealsVisibilityField({
  value,
  onChange,
}: {
  value: DealsVisibility;
  onChange: (value: DealsVisibility) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border p-3 bg-muted/20">
      <Label className="text-sm">Visibilidade em Negociações</Label>
      <p className="text-xs text-muted-foreground">
        Define quais negociações o membro enxerga nos funis aos quais tem acesso.
      </p>
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as DealsVisibility)}
        className="gap-2"
      >
        <div className="flex items-start gap-2">
          <RadioGroupItem value="all" id="deals-vis-all" className="mt-0.5" />
          <label htmlFor="deals-vis-all" className="text-sm cursor-pointer leading-snug">
            Todas as negociações dos funis permitidos
          </label>
        </div>
        <div className="flex items-start gap-2">
          <RadioGroupItem value="assigned_only" id="deals-vis-assigned" className="mt-0.5" />
          <label htmlFor="deals-vis-assigned" className="text-sm cursor-pointer leading-snug">
            Apenas negociações de leads atribuídos a este membro (ou onde ele é responsável)
          </label>
        </div>
      </RadioGroup>
    </div>
  );
}

function MemberFunnelAccessField({
  funnels,
  selectedFunnelIds,
  onChange,
}: {
  funnels: { id: string; name: string }[];
  selectedFunnelIds: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <div className="space-y-3">
      <Label>Funis acessíveis</Label>
      <p className="text-xs text-muted-foreground">
        Selecione os funis que este membro poderá ver no CRM e Negociações. Se nenhum for
        selecionado, terá acesso a todos.
      </p>
      <div className="grid gap-2 max-h-48 overflow-y-auto rounded border p-2">
        {funnels.length === 0 && (
          <p className="text-xs text-muted-foreground">Nenhum funil cadastrado.</p>
        )}
        {funnels.map((f) => (
          <div key={f.id} className="flex items-center space-x-3">
            <Checkbox
              id={`funnel-${f.id}`}
              checked={selectedFunnelIds.includes(f.id)}
              onCheckedChange={(checked) =>
                onChange(
                  checked
                    ? [...selectedFunnelIds, f.id]
                    : selectedFunnelIds.filter((id) => id !== f.id),
                )
              }
            />
            <label htmlFor={`funnel-${f.id}`} className="text-sm cursor-pointer">
              {f.name}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberCalendarAccessField({
  syncCalendars,
  selectedCalendarIds,
  onChange,
  profile,
}: {
  syncCalendars: { id: string; display_name: string | null; calendar_id: string }[];
  selectedCalendarIds: string[];
  onChange: (ids: string[]) => void;
  profile: MemberProfile;
}) {
  return (
    <div className="space-y-3">
      <Label>Agendas acessíveis</Label>
      <p className="text-xs text-muted-foreground">
        Selecione as agendas Google que este membro poderá ver em Agendamentos.
        {profile === "gestor"
          ? " Perfil gestor enxerga todas as agendas independentemente desta lista."
          : " Se nenhuma for selecionada, terá acesso a todas."}
      </p>
      <div className="grid gap-2 max-h-48 overflow-y-auto rounded border p-2">
        {syncCalendars.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Nenhuma agenda Google habilitada no workspace.
          </p>
        )}
        {syncCalendars.map((cal) => (
          <div key={cal.id} className="flex items-center space-x-3">
            <Checkbox
              id={`calendar-${cal.id}`}
              checked={selectedCalendarIds.includes(cal.id)}
              onCheckedChange={(checked) =>
                onChange(
                  checked
                    ? [...selectedCalendarIds, cal.id]
                    : selectedCalendarIds.filter((id) => id !== cal.id),
                )
              }
            />
            <label htmlFor={`calendar-${cal.id}`} className="text-sm cursor-pointer">
              {cal.display_name || cal.calendar_id}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelGroupIcon({ channel }: { channel: "whatsapp" | "instagram" | "messenger" }) {
  if (channel === "whatsapp") return <Phone className="h-4 w-4" />;
  if (channel === "instagram") return <Instagram className="h-4 w-4" />;
  return <MessengerIcon className="h-4 w-4" />;
}

function MemberIntegrationAccessField({
  integrations,
  selectedIntegrationIds,
  onChange,
}: {
  integrations: MessagingIntegrationOption[];
  selectedIntegrationIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const grouped = groupMessagingIntegrationsByChannel(integrations);

  const toggleGroup = (channel: "whatsapp" | "instagram" | "messenger", checked: boolean) => {
    const groupIds = grouped[channel].map((item) => item.id);
    if (checked) {
      onChange([...new Set([...selectedIntegrationIds, ...groupIds])]);
      return;
    }
    onChange(selectedIntegrationIds.filter((id) => !groupIds.includes(id)));
  };

  const isGroupChecked = (channel: "whatsapp" | "instagram" | "messenger") => {
    const groupIds = grouped[channel].map((item) => item.id);
    return groupIds.length > 0 && groupIds.every((id) => selectedIntegrationIds.includes(id));
  };

  const isGroupIndeterminate = (channel: "whatsapp" | "instagram" | "messenger") => {
    const groupIds = grouped[channel].map((item) => item.id);
    const selectedCount = groupIds.filter((id) => selectedIntegrationIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < groupIds.length;
  };

  return (
    <div className="space-y-3">
      <Label>Canais visíveis na inbox</Label>
      <p className="text-xs text-muted-foreground">
        Escolha quais números e contas este membro pode ver em Conversas. Se o workspace já usa
        restrição por canal, deixar tudo desmarcado remove o acesso à inbox de mensagens. Em
        workspaces que nunca configuraram canais por membro, todos continuam vendo tudo.
      </p>
      <div className="space-y-3 max-h-64 overflow-y-auto rounded border p-2">
        {integrations.length === 0 && (
          <p className="text-xs text-muted-foreground">Nenhum canal de mensagem conectado.</p>
        )}
        {MESSAGING_CHANNEL_GROUPS.map((group) => {
          const items = grouped[group.channel];
          if (items.length === 0) return null;

          return (
            <div key={group.channel} className="space-y-2">
              <div className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2 py-1.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ChannelGroupIcon channel={group.channel} />
                  {group.label}
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`channel-group-${group.channel}`}
                    checked={isGroupChecked(group.channel)}
                    ref={(el) => {
                      if (el) {
                        (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate =
                          isGroupIndeterminate(group.channel);
                      }
                    }}
                    onCheckedChange={(checked) => toggleGroup(group.channel, checked === true)}
                  />
                  <label
                    htmlFor={`channel-group-${group.channel}`}
                    className="text-xs text-muted-foreground cursor-pointer"
                  >
                    Todos
                  </label>
                </div>
              </div>
              <div className="grid gap-2 pl-2">
                {items.map((integration) => {
                  const detail = getMessagingIntegrationDetail(integration);
                  return (
                    <div key={integration.id} className="flex items-start space-x-3">
                      <Checkbox
                        id={`integration-${integration.id}`}
                        checked={selectedIntegrationIds.includes(integration.id)}
                        onCheckedChange={(checked) =>
                          onChange(
                            checked
                              ? [...selectedIntegrationIds, integration.id]
                              : selectedIntegrationIds.filter((id) => id !== integration.id),
                          )
                        }
                        className="mt-0.5"
                      />
                      <label htmlFor={`integration-${integration.id}`} className="text-sm cursor-pointer leading-snug">
                        <span className="font-medium">{getMessagingIntegrationLabel(integration)}</span>
                        {detail ? (
                          <span className="block text-xs text-muted-foreground">{detail}</span>
                        ) : null}
                        {integration.status === "disconnected" ? (
                          <span className="block text-xs text-amber-600">Desconectado</span>
                        ) : null}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export function WorkspaceMembersManager({ workspaceId }: WorkspaceMembersManagerProps) {
  const { user } = useAuth();
  const { workspaceMetadata } = useWorkspace(user?.id);
  const multiGcalEnabled = isGcalMultiCalendarEnabled(workspaceMetadata);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [memberLimit, setMemberLimit] = useState<{ current: number; limit: number; plan: string } | null>(null);
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [aiSignatureEnabled, setAiSignatureEnabled] = useState(false);
  const [aiSignatureLoading, setAiSignatureLoading] = useState(false);
  
  // Form states
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editConfirmPassword, setEditConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [editPasswordSectionOpen, setEditPasswordSectionOpen] = useState(false);
  const [permissions, setPermissions] = useState<MemberPermissions>(NEW_MEMBER_DEFAULT_PERMISSIONS);
  const [funnels, setFunnels] = useState<{ id: string; name: string }[]>([]);
  const [selectedFunnelIds, setSelectedFunnelIds] = useState<string[]>([]);
  const [initialFunnelIds, setInitialFunnelIds] = useState<string[]>([]);
  const [defaultFunnelId, setDefaultFunnelId] = useState<string | null>(null);
  const [defaultStageSlug, setDefaultStageSlug] = useState<string | null>(null);
  const [defaultFunnelStages, setDefaultFunnelStages] = useState<
    { slug: string; name: string }[]
  >([]);
  const [syncCalendars, setSyncCalendars] = useState<{ id: string; display_name: string | null; calendar_id: string }[]>([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const [initialCalendarIds, setInitialCalendarIds] = useState<string[]>([]);
  const [messagingIntegrations, setMessagingIntegrations] = useState<MessagingIntegrationOption[]>([]);
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<string[]>([]);
  const [initialIntegrationIds, setInitialIntegrationIds] = useState<string[]>([]);
  const showCalendarAssignment = multiGcalEnabled && syncCalendars.length > 1;
  
  const { toast } = useToast();
  const { checkCanCreate } = useResourceLimits(workspaceId);
  const { isOwner: isAdminMaster } = useUserRole();
  const [currentMemberRole, setCurrentMemberRole] = useState<string | null>(null);
  const canConfigureChannelAccess = currentMemberRole === "owner";

  // Fetch member limits
  const fetchMemberLimits = async () => {
    if (!workspaceId) return;
    const result = await checkCanCreate('team_members');
    setMemberLimit({ current: result.current, limit: result.limit, plan: result.plan });
  };

  const getValidSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const { error } = await supabase.auth.getUser();
    if (error) {
      await supabase.auth.signOut({ scope: "local" });
      return null;
    }

    return session;
  };

  const callManageWorkspaceMembers = async (body: Record<string, unknown>) => {
    const session = await getValidSession();
    if (!session?.access_token) throw new Error("Sessão expirada. Faça login novamente.");

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-workspace-members`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify(body),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || result?.message || `Erro ${response.status}`);
    return result;
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const data = await callManageWorkspaceMembers({ action: "list", workspaceId });
      const membersList = data.members || [];
      setMembers(membersList);

      const currentMember = membersList.find((m: Member) => m.user_id === user?.id);
      setCurrentMemberRole(currentMember?.role ?? null);
    } catch (error: any) {
      console.error("Erro ao buscar membros:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível carregar os membros",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch signature setting
  const fetchSignatureSetting = async () => {
    if (!workspaceId) return;
    const { data } = await supabase
      .from("workspaces")
      .select("member_signature_enabled, ai_signature_enabled")
      .eq("id", workspaceId)
      .single();
    if (data) {
      setSignatureEnabled(data.member_signature_enabled ?? false);
      setAiSignatureEnabled((data as any).ai_signature_enabled ?? false);
    }
  };

  const handleToggleSignature = async (enabled: boolean) => {
    setSignatureLoading(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({ member_signature_enabled: enabled })
        .eq("id", workspaceId);
      if (error) throw error;
      setSignatureEnabled(enabled);
      toast({ title: enabled ? "Assinatura ativada" : "Assinatura desativada" });
    } catch (error: any) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleToggleAiSignature = async (enabled: boolean) => {
    setAiSignatureLoading(true);
    try {
      if (enabled) {
        const { data: agents } = await supabase
          .from("agents")
          .select("name, display_name")
          .eq("workspace_id", workspaceId);
        const missing = (agents || []).filter(
          (a: any) => !a.display_name || !String(a.display_name).trim(),
        );
        if (missing.length > 0) {
          const names = missing.map((a: any) => a.name).join(", ");
          toast({
            title: "Agente sem nome de apresentação",
            description: `Defina o nome de apresentação em: ${names}. A assinatura só é enviada quando o agente tem nome de apresentação configurado.`,
            variant: "destructive",
          });
        }
      }
      const { error } = await supabase
        .from("workspaces")
        .update({ ai_signature_enabled: enabled } as any)
        .eq("id", workspaceId);
      if (error) throw error;
      setAiSignatureEnabled(enabled);
      toast({ title: enabled ? "Assinatura do agente ativada" : "Assinatura do agente desativada" });
    } catch (error: any) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
    } finally {
      setAiSignatureLoading(false);
    }
  };

  useEffect(() => {
    if (workspaceId && user) {
      fetchMembers();
      fetchMemberLimits();
      fetchSignatureSetting();
      // Load funnels for the access selector
      supabase
        .from("sales_funnels")
        .select("id, name")
        .eq("workspace_id", workspaceId)
        .order("order_index", { ascending: true })
        .then(({ data }) => setFunnels(data || []));

      supabase
        .from("integrations")
        .select("id, provider, config, instance_identifier, instagram_account_id, page_id, status")
        .eq("workspace_id", workspaceId)
        .in("status", ["active", "pending", "disconnected"])
        .in("provider", [
          "whatsapp_evolution",
          "whatsapp_cloud",
          "whatsapp_uazapi",
          "instagram",
          "messenger",
        ])
        .then(({ data }) => setMessagingIntegrations((data || []) as MessagingIntegrationOption[]));

      if (multiGcalEnabled) {
        supabase
          .from("google_calendar_sync_calendars")
          .select("id, display_name, calendar_id")
          .eq("workspace_id", workspaceId)
          .eq("is_enabled", true)
          .order("is_primary", { ascending: false })
          .order("created_at", { ascending: true })
          .then(({ data }) => setSyncCalendars(data || []));
      } else {
        setSyncCalendars([]);
      }
    }
  }, [workspaceId, user?.id, multiGcalEnabled]);

  useEffect(() => {
    if (!defaultFunnelId) {
      setDefaultFunnelStages([]);
      setDefaultStageSlug(null);
      return;
    }

    supabase
      .from("lead_stages")
      .select("slug, name")
      .eq("funnel_id", defaultFunnelId)
      .order("order_index", { ascending: true })
      .then(({ data }) => {
        const stages = data || [];
        setDefaultFunnelStages(stages);
        setDefaultStageSlug((prev) =>
          prev && stages.some((s) => s.slug === prev) ? prev : null
        );
      });
  }, [defaultFunnelId]);

  /**
   * Concede acessos a funis ANTES de gravar o membro.
   * O banco valida que o funil padrão esteja entre os funis permitidos,
   * então as liberações precisam existir antes do update.
   */
  const grantMemberFunnelAccess = async (memberId: string) => {
    const toAdd = selectedFunnelIds.filter((id) => !initialFunnelIds.includes(id));
    if (toAdd.length === 0) return;
    await supabase.from("workspace_member_funnels").upsert(
      toAdd.map((funnel_id) => ({
        member_id: memberId,
        funnel_id,
        workspace_id: workspaceId,
      })),
      { onConflict: "member_id,funnel_id", ignoreDuplicates: true },
    );
  };

  const syncMemberFunnelAndCalendarAccess = async (memberId: string) => {
    const toRemove = initialFunnelIds.filter((id) => !selectedFunnelIds.includes(id));
    if (toRemove.length > 0) {
      await supabase
        .from("workspace_member_funnels")
        .delete()
        .eq("member_id", memberId)
        .in("funnel_id", toRemove);
    }
    await grantMemberFunnelAccess(memberId);


    if (multiGcalEnabled && permissions.appointments) {
      const calendarsToAdd = selectedCalendarIds.filter((id) => !initialCalendarIds.includes(id));
      const calendarsToRemove = initialCalendarIds.filter((id) => !selectedCalendarIds.includes(id));
      if (calendarsToRemove.length > 0) {
        await supabase
          .from("workspace_member_calendars")
          .delete()
          .eq("member_id", memberId)
          .in("sync_calendar_id", calendarsToRemove);
      }
      if (calendarsToAdd.length > 0) {
        await supabase.from("workspace_member_calendars").insert(
          calendarsToAdd.map((sync_calendar_id) => ({
            member_id: memberId,
            sync_calendar_id,
            workspace_id: workspaceId,
          })),
        );
      }
    } else if (initialCalendarIds.length > 0) {
      await supabase.from("workspace_member_calendars").delete().eq("member_id", memberId);
    }

    if (canConfigureChannelAccess) {
      const integrationsToAdd = selectedIntegrationIds.filter((id) => !initialIntegrationIds.includes(id));
      const integrationsToRemove = initialIntegrationIds.filter((id) => !selectedIntegrationIds.includes(id));
      if (integrationsToRemove.length > 0) {
        await supabase
          .from("workspace_member_integrations")
          .delete()
          .eq("member_id", memberId)
          .in("integration_id", integrationsToRemove);
      }
      if (integrationsToAdd.length > 0) {
        await supabase.from("workspace_member_integrations").insert(
          integrationsToAdd.map((integration_id) => ({
            member_id: memberId,
            integration_id,
            workspace_id: workspaceId,
          })),
        );
      }
    } else if (initialIntegrationIds.length > 0) {
      await supabase.from("workspace_member_integrations").delete().eq("member_id", memberId);
    }
  };

  const syncMemberIntegrationAccess = async (memberId: string) => {
    if (!canConfigureChannelAccess) return;

    const integrationsToAdd = selectedIntegrationIds.filter((id) => !initialIntegrationIds.includes(id));
    const integrationsToRemove = initialIntegrationIds.filter((id) => !selectedIntegrationIds.includes(id));
    if (integrationsToRemove.length > 0) {
      await supabase
        .from("workspace_member_integrations")
        .delete()
        .eq("member_id", memberId)
        .in("integration_id", integrationsToRemove);
    }
    if (integrationsToAdd.length > 0) {
      await supabase.from("workspace_member_integrations").insert(
        integrationsToAdd.map((integration_id) => ({
          member_id: memberId,
          integration_id,
          workspace_id: workspaceId,
        })),
      );
    }
  };

  const resetForm = () => {
    setDisplayName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setEditPassword("");
    setEditConfirmPassword("");
    setShowPassword(false);
    setEditPasswordSectionOpen(false);
    setPermissions(NEW_MEMBER_DEFAULT_PERMISSIONS);
    setSelectedMember(null);
    setSelectedFunnelIds([]);
    setInitialFunnelIds([]);
    setDefaultFunnelId(null);
    setDefaultStageSlug(null);
    setDefaultFunnelStages([]);
    setSelectedCalendarIds([]);
    setInitialCalendarIds([]);
    setSelectedIntegrationIds([]);
    setInitialIntegrationIds([]);
  };

  const handleCreateMember = async () => {
    // Validações
    if (!displayName.trim()) {
      toast({ title: "Nome obrigatório", variant: "destructive" });
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      toast({ title: "Email inválido", variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: "Senha deve ter no mínimo 6 caracteres", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Senhas não coincidem", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const result = await callManageWorkspaceMembers({
        action: "create",
        workspaceId,
        email: email.trim().toLowerCase(),
        password,
        displayName: displayName.trim(),
        permissions
      });

      const { data: newMemberRow } = await supabase
        .from("workspace_members")
        .select("id")
        .eq("workspace_id", workspaceId)
        .eq("user_id", result.memberId)
        .single();

      if (newMemberRow?.id) {
        await syncMemberFunnelAndCalendarAccess(newMemberRow.id);
      }
      dispatchInboxScopeChanged({ workspaceId, reason: "visibility" });

      toast({
        title: "Membro criado!",
        description: `${displayName} foi adicionado à equipe`
      });

      setIsCreateDialogOpen(false);
      resetForm();
      invalidateWorkspaceCache();
      fetchMembers();
      fetchMemberLimits(); // Atualizar contador
    } catch (error: any) {
      console.error("Erro ao criar membro:", error);
      toast({
        title: "Erro ao criar membro",
        description: error.message || "Tente novamente",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;

    const canResetPassword = canResetMemberPassword(selectedMember);

    if (canResetPassword && (editPassword || editConfirmPassword)) {
      if (editPassword.length < 6) {
        toast({ title: "Senha deve ter no mínimo 6 caracteres", variant: "destructive" });
        return;
      }
      if (editPassword !== editConfirmPassword) {
        toast({ title: "Senhas não coincidem", variant: "destructive" });
        return;
      }
    }

    setSubmitting(true);
    try {
      const isOwnerRole = selectedMember.role === "owner";
      const body: Record<string, unknown> = {
        action: "update",
        memberId: selectedMember.id,
        displayName: displayName.trim() || undefined,
        defaultFunnelId: defaultFunnelId || null,
        defaultStageSlug: defaultFunnelId ? defaultStageSlug || null : null,
      };
      // Só enviar permissions para membros (nunca para owner)
      if (!isOwnerRole) {
        body.permissions = {
          ...(selectedMember.permissions as unknown as Record<string, unknown> | null),
          ...permissions,
        };
      }
      if (canResetPassword && editPassword) {
        body.password = editPassword;
      }

      // Liberar funis antes de gravar (validação do funil padrão no banco)
      if (!isOwnerRole && selectedMember.role !== "admin") {
        await grantMemberFunnelAccess(selectedMember.id);
      }

      await callManageWorkspaceMembers(body);

      if (!isOwnerRole && selectedMember.role !== "admin") {
        await syncMemberFunnelAndCalendarAccess(selectedMember.id);
      } else if (!isOwnerRole && selectedMember.role === "admin") {
        await syncMemberIntegrationAccess(selectedMember.id);
      }

      dispatchInboxScopeChanged({ workspaceId, reason: "visibility" });

      const pwdChanged = canResetPassword && !!editPassword;
      toast({
        title: "Membro atualizado!",
        description: isOwnerRole
          ? "Nome de exibição atualizado"
          : pwdChanged
            ? "Dados e senha atualizados com sucesso"
            : "Permissões atualizadas com sucesso",
      });

      setIsEditDialogOpen(false);
      resetForm();
      invalidateWorkspaceCache();
      fetchMembers();
    } catch (error: any) {
      console.error("Erro ao atualizar membro:", error);
      toast({
        title: "Erro ao atualizar",
        description: error.message || "Tente novamente",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMember = async () => {
    if (!selectedMember) return;

    setSubmitting(true);
    try {
      await callManageWorkspaceMembers({
        action: "delete",
        memberId: selectedMember.id
      });

      toast({
        title: "Membro removido",
        description: `${selectedMember.display_name || selectedMember.email} foi removido da equipe`
      });

      setIsDeleteDialogOpen(false);
      resetForm();
      invalidateWorkspaceCache();
      fetchMembers();
      fetchMemberLimits(); // Atualizar contador
      dispatchInboxScopeChanged({ workspaceId, reason: "visibility" });
    } catch (error: any) {
      console.error("Erro ao remover membro:", error);
      toast({
        title: "Erro ao remover",
        description: error.message || "Tente novamente",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = async (member: Member) => {
    setSelectedMember(member);
    setDisplayName(member.display_name || "");
    setEditPassword("");
    setEditConfirmPassword("");
    setEditPasswordSectionOpen(false);
    setPermissions(mergeMemberPermissions(member.permissions));
    setSelectedFunnelIds([]);
    setInitialFunnelIds([]);
    setSelectedCalendarIds([]);
    setInitialCalendarIds([]);
    setSelectedIntegrationIds([]);
    setInitialIntegrationIds([]);
    setDefaultFunnelId(member.default_funnel_id ?? null);
    setDefaultStageSlug(member.default_stage_slug ?? null);
    if (member.role !== "owner" && member.role !== "admin") {
      const { data } = await supabase
        .from("workspace_member_funnels")
        .select("funnel_id")
        .eq("member_id", member.id);
      const ids = (data || []).map((r: any) => r.funnel_id);
      setSelectedFunnelIds(ids);
      setInitialFunnelIds(ids);
    }
    if (multiGcalEnabled && member.role !== "owner" && member.role !== "admin") {
      const { data: calendarRows } = await supabase
        .from("workspace_member_calendars")
        .select("sync_calendar_id")
        .eq("member_id", member.id);
      const calendarIds = (calendarRows || []).map((r) => r.sync_calendar_id);
      setSelectedCalendarIds(calendarIds);
      setInitialCalendarIds(calendarIds);
    }
    if (canConfigureChannelAccess && member.role !== "owner") {
      const { data: integrationRows } = await supabase
        .from("workspace_member_integrations")
        .select("integration_id")
        .eq("member_id", member.id);
      const integrationIds = (integrationRows || []).map((r) => r.integration_id);
      setSelectedIntegrationIds(integrationIds);
      setInitialIntegrationIds(integrationIds);
    }
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (member: Member) => {
    setSelectedMember(member);
    setIsDeleteDialogOpen(true);
  };

  const formatLastActive = (date: string | null) => {
    if (!date) return "Nunca";
    return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  /** Badge na lista: role do workspace (owner/admin) ou perfil configurado (gestor/comercial/suporte). */
  const getMemberBadge = (member: Member) => {
    if (member.role === "owner") {
      return (
        <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
          <Crown className="mr-1 h-3 w-3" />
          Proprietário
        </Badge>
      );
    }

    if (member.role === "admin") {
      return (
        <Badge className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20">
          <Shield className="mr-1 h-3 w-3" />
          Administrador
        </Badge>
      );
    }

    const profile = detectMemberProfile(mergeMemberPermissions(member.permissions));

    switch (profile) {
      case "gestor":
        return (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
            {MEMBER_PROFILE_LABELS.gestor}
          </Badge>
        );
      case "comercial":
        return (
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            {MEMBER_PROFILE_LABELS.comercial}
          </Badge>
        );
      case "suporte":
        return (
          <Badge className="bg-violet-500/10 text-violet-600 border-violet-500/20">
            {MEMBER_PROFILE_LABELS.suporte}
          </Badge>
        );
      default:
        return <Badge variant="outline">Membro</Badge>;
    }
  };

  // Determine button visibility and state based on plan
  const isEssentialPlan = memberLimit?.plan === 'inicial';
  const isLimitReached = memberLimit ? memberLimit.current >= memberLimit.limit : false;
  const canManageMembers =
    isAdminMaster || currentMemberRole === "owner" || currentMemberRole === "admin";
  const canEditMember = (member: Member) =>
    canManageMembers && (member.role === "owner" ? member.user_id === user?.id : true);
  const canShowAddButton = canManageMembers || !isEssentialPlan;
  const canAddMember = isAdminMaster || !isLimitReached;

  /** Colaboradores (role member) podem ter senha redefinida pelo gestor. */
  const canResetMemberPassword = (member: Member) =>
    member.role !== "owner" && member.role !== "admin";

  const getPlanDisplayName = (plan: string) => {
    const names: Record<string, string> = {
      'inicial': 'Essencial',
      'intermediario': 'Plus',
      'pro': 'PRO',
      'enterprise': 'Enterprise'
    };
    return names[plan] || plan;
  };

  return (
    <div className="space-y-4">
      {/* Signature Toggle */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">Assinatura do membro nas mensagens</Label>
          <p className="text-xs text-muted-foreground">
            Quando ativado, mensagens manuais incluem o nome do membro como assinatura
          </p>
        </div>
        <Switch
          checked={signatureEnabled}
          onCheckedChange={handleToggleSignature}
          disabled={signatureLoading || !canManageMembers}
        />
      </div>

      {/* AI Agent Signature Toggle */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">Assinatura do agente de IA nas mensagens</Label>
          <p className="text-xs text-muted-foreground">
            Quando ativado, mensagens enviadas pela IA incluem o nome do agente como assinatura
          </p>
        </div>
        <Switch
          checked={aiSignatureEnabled}
          onCheckedChange={handleToggleAiSignature}
          disabled={aiSignatureLoading || !canManageMembers}
        />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Membros da Equipe</h4>
          <p className="text-sm text-muted-foreground">
            Gerencie os colaboradores do seu workspace
            {memberLimit && !canManageMembers && !isEssentialPlan && (
              <span className="ml-2 text-xs">
                ({memberLimit.current}/{memberLimit.limit} membros)
              </span>
            )}
          </p>
        </div>
        
        {/* Plano Essencial - Mostrar mensagem de upgrade */}
        {isEssentialPlan && !canManageMembers ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-4 py-2 rounded-lg">
            <Lock className="w-4 h-4" />
            <span>Faça upgrade para adicionar membros</span>
          </div>
        ) : canShowAddButton ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={() => { resetForm(); setIsCreateDialogOpen(true); }}
                    disabled={!canAddMember}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Adicionar Membro
                  </Button>
                </div>
              </TooltipTrigger>
              {!canAddMember && (
                <TooltipContent>
                  <p>Limite de {memberLimit?.limit} membros atingido. Faça upgrade do plano.</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>

      {/* Lista de Membros */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Nenhum membro na equipe</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {member.display_name?.[0]?.toUpperCase() ||
                      member.email?.[0]?.toUpperCase() ||
                      "?"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {member.display_name || member.email?.split("@")[0] || "Membro"}
                    </span>
                    {getMemberBadge(member)}
                  </div>
                  <p className="text-sm text-muted-foreground">{member.email}</p>
                  <p className="text-xs text-muted-foreground">
                    Último acesso: {formatLastActive(member.last_sign_in_at || member.last_active_at)}
                  </p>
                </div>
              </div>

              {canEditMember(member) && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(member)}
                    title={
                      member.role === "owner"
                        ? "Editar seu nome de exibição"
                        : "Editar membro e senha"
                    }
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  {member.role !== "owner" && member.role !== "admin" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(member)}
                      className="text-destructive hover:text-destructive"
                      title="Remover membro"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog de Criar Membro */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Membro</DialogTitle>
            <DialogDescription>
              Crie uma conta para um colaborador da equipe
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">Nome de Exibição</Label>
              <Input
                id="displayName"
                placeholder="Nome do colaborador"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Senha</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Confirme a senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <MemberProfileField
              value={detectMemberProfile(permissions)}
              onChange={(profile) =>
                setPermissions((prev) => applyMemberProfile(profile, prev))
              }
            />

            <div className="space-y-3">
              <Label>Permissões de Acesso</Label>
              <div className="grid gap-2">
                {PERMISSION_OPTIONS.map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center space-x-3">
                    <Checkbox
                      id={`perm-${key}`}
                      checked={!!permissions[key as keyof MemberPermissions]}
                      onCheckedChange={(checked) =>
                        setPermissions(prev => ({ ...prev, [key]: !!checked, member_profile: "custom" }))
                      }
                    />
                    <label
                      htmlFor={`perm-${key}`}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      {label}
                    </label>
                  </div>
                ))}
              </div>
              {permissions.crm && (
                <div className="flex items-center space-x-3 pt-1">
                  <Checkbox
                    id="perm-crm-manage-funnels"
                    checked={!!permissions.crm_manage_funnels}
                    onCheckedChange={(checked) =>
                      setPermissions(prev => ({ ...prev, crm_manage_funnels: !!checked, member_profile: "custom" }))
                    }
                  />
                  <label htmlFor="perm-crm-manage-funnels" className="text-sm cursor-pointer">
                    Pode criar e editar funis no CRM
                  </label>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                * O membro não terá acesso a pagamentos e plano contratado
              </p>
            </div>


            {permissions.conversations && (
              <ConversationsVisibilityField
                value={permissions.conversations_visibility}
                onChange={(conversations_visibility) =>
                  setPermissions((prev) => ({ ...prev, conversations_visibility, member_profile: "custom" }))
                }
              />
            )}

            {permissions.appointments && (
              <AppointmentsCalendarVisibilityField
                value={permissions.appointments_calendar_visibility ?? "all"}
                onChange={(appointments_calendar_visibility) =>
                  setPermissions((prev) => ({
                    ...prev,
                    appointments_calendar_visibility,
                    member_profile: "custom",
                  }))
                }
              />
            )}

            {permissions.deals && (
              <DealsVisibilityField
                value={permissions.deals_visibility ?? "all"}
                onChange={(deals_visibility) =>
                  setPermissions((prev) => ({ ...prev, deals_visibility, member_profile: "custom" }))
                }
              />
            )}

            {(permissions.crm || permissions.deals) && (
              <MemberFunnelAccessField
                funnels={funnels}
                selectedFunnelIds={selectedFunnelIds}
                onChange={setSelectedFunnelIds}
              />
            )}

            {showCalendarAssignment && permissions.appointments && (
              <MemberCalendarAccessField
                syncCalendars={syncCalendars}
                selectedCalendarIds={selectedCalendarIds}
                onChange={setSelectedCalendarIds}
                profile={detectMemberProfile(permissions)}
              />
            )}

            {canConfigureChannelAccess && permissions.conversations && messagingIntegrations.length > 0 && (
              <MemberIntegrationAccessField
                integrations={messagingIntegrations}
                selectedIntegrationIds={selectedIntegrationIds}
                onChange={setSelectedIntegrationIds}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateMember} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Criar Membro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Editar Membro */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedMember?.role === "owner" ? "Editar Nome de Exibição" : "Editar Membro"}
            </DialogTitle>
            <DialogDescription>
              {selectedMember?.role === "owner"
                ? `Altere o nome de exibição de ${selectedMember?.display_name || selectedMember?.email}`
                : selectedMember && canResetMemberPassword(selectedMember)
                  ? `Altere permissões e defina uma nova senha de acesso para ${selectedMember.display_name || selectedMember.email}`
                  : `Altere as permissões de ${selectedMember?.display_name || selectedMember?.email}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editDisplayName">Nome de Exibição</Label>
              <Input
                id="editDisplayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            {selectedMember && selectedMember.role !== "owner" && (
              <div className="space-y-2">
                <Label htmlFor="editEmail">Email de acesso</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="editEmail"
                    type="email"
                    value={selectedMember.email}
                    readOnly
                    className="pl-10 bg-muted"
                  />
                </div>
              </div>
            )}

            {selectedMember && canResetMemberPassword(selectedMember) && (
              <Collapsible
                open={editPasswordSectionOpen}
                onOpenChange={setEditPasswordSectionOpen}
                className="rounded-lg border bg-muted/30"
              >
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-between px-3 py-2.5 h-auto text-foreground hover:bg-muted hover:text-foreground"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Lock className="w-4 h-4 text-muted-foreground" />
                      Alterar senha de acesso
                    </span>
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 text-muted-foreground shrink-0 transition-transform",
                        editPasswordSectionOpen && "rotate-180",
                      )}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-3 pb-3 space-y-3">
                  <p className="text-xs text-muted-foreground">
                    A senha atual não pode ser exibida. Informe uma nova senha (mín. 6 caracteres)
                    ou deixe em branco para não alterar.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="editPassword">Nova senha</Label>
                    <div className="relative">
                      <Input
                        id="editPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Nova senha de acesso"
                        value={editPassword}
                        onChange={(e) => setEditPassword(e.target.value)}
                        className="pr-10 bg-background"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="editConfirmPassword">Confirmar nova senha</Label>
                    <Input
                      id="editConfirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Repita a nova senha"
                      value={editConfirmPassword}
                      onChange={(e) => setEditConfirmPassword(e.target.value)}
                      className="bg-background"
                      autoComplete="new-password"
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {selectedMember?.role !== "owner" && selectedMember?.role !== "admin" && (
              <MemberProfileField
                value={detectMemberProfile(permissions)}
                onChange={(profile) =>
                  setPermissions((prev) => applyMemberProfile(profile, prev))
                }
              />
            )}

            {selectedMember?.role !== "owner" && (
              <div className="space-y-3">
                <Label>Permissões de Acesso</Label>
                <div className="grid gap-2">
                  {PERMISSION_OPTIONS.map(({ key, label, icon: Icon }) => (
                    <div key={key} className="flex items-center space-x-3">
                      <Checkbox
                        id={`edit-perm-${key}`}
                        checked={!!permissions[key as keyof MemberPermissions]}
                        onCheckedChange={(checked) =>
                          setPermissions(prev => ({ ...prev, [key]: !!checked, member_profile: "custom" }))
                        }
                      />
                      <label
                        htmlFor={`edit-perm-${key}`}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedMember?.role !== "admin" && permissions.crm && (
                  <div className="flex items-center space-x-3 pt-1">
                    <Checkbox
                      id="edit-perm-crm-manage-funnels"
                      checked={!!permissions.crm_manage_funnels}
                      onCheckedChange={(checked) =>
                        setPermissions(prev => ({ ...prev, crm_manage_funnels: !!checked, member_profile: "custom" }))
                      }
                    />
                    <label htmlFor="edit-perm-crm-manage-funnels" className="text-sm cursor-pointer">
                      Pode criar e editar funis no CRM
                    </label>
                  </div>
                )}
              </div>
            )}


            {selectedMember?.role !== "owner" &&
              selectedMember?.role !== "admin" &&
              permissions.conversations && (
                <ConversationsVisibilityField
                  value={permissions.conversations_visibility}
                  onChange={(conversations_visibility) =>
                    setPermissions((prev) => ({ ...prev, conversations_visibility, member_profile: "custom" }))
                  }
                />
              )}

            {selectedMember?.role !== "owner" &&
              selectedMember?.role !== "admin" &&
              permissions.appointments && (
                <AppointmentsCalendarVisibilityField
                  value={permissions.appointments_calendar_visibility ?? "all"}
                  onChange={(appointments_calendar_visibility) =>
                    setPermissions((prev) => ({
                      ...prev,
                      appointments_calendar_visibility,
                      member_profile: "custom",
                    }))
                  }
                />
              )}

            {selectedMember?.role !== "owner" &&
              selectedMember?.role !== "admin" &&
              permissions.deals && (
                <DealsVisibilityField
                  value={permissions.deals_visibility ?? "all"}
                  onChange={(deals_visibility) =>
                    setPermissions((prev) => ({ ...prev, deals_visibility, member_profile: "custom" }))
                  }
                />
              )}

            {selectedMember?.role !== "owner" &&
              selectedMember?.role !== "admin" &&
              (permissions.crm || permissions.deals) && (
                <MemberFunnelAccessField
                  funnels={funnels}
                  selectedFunnelIds={selectedFunnelIds}
                  onChange={(ids) => {
                    if (defaultFunnelId && !ids.includes(defaultFunnelId)) {
                      setDefaultFunnelId(null);
                      setDefaultStageSlug(null);
                    }
                    setSelectedFunnelIds(ids);
                  }}
                />
              )}

            {selectedMember?.role !== "owner" &&
              selectedMember?.role !== "admin" &&
              (permissions.crm || permissions.conversations) && (
                <div className="space-y-2">
                  <Label>Funil padrão de atribuição</Label>
                  <p className="text-xs text-muted-foreground">
                    Quando outro membro transferir um lead para esta pessoa, o lead será movido
                    para este funil e coluna. Se não definir, a atribuição mantém o lead no funil
                    atual.
                  </p>
                  <Select
                    value={defaultFunnelId ?? "__none__"}
                    onValueChange={(v) => {
                      const nextFunnelId = v === "__none__" ? null : v;
                      setDefaultFunnelId(nextFunnelId);
                      if (!nextFunnelId) setDefaultStageSlug(null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um funil" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Não definido</SelectItem>
                      {(selectedFunnelIds.length > 0
                        ? funnels.filter((f) => selectedFunnelIds.includes(f.id))
                        : funnels
                      ).map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {defaultFunnelId && defaultFunnelStages.length > 0 && (
                    <div className="space-y-2 pt-1">
                      <Label className="text-sm">Coluna padrão</Label>
                      <Select
                        value={defaultStageSlug ?? "__first__"}
                        onValueChange={(v) =>
                          setDefaultStageSlug(v === "__first__" ? null : v)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Primeira coluna do funil" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__first__">
                            Primeira coluna ({defaultFunnelStages[0]?.name})
                          </SelectItem>
                          {defaultFunnelStages.map((stage) => (
                            <SelectItem key={stage.slug} value={stage.slug}>
                              {stage.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

            {selectedMember?.role !== "owner" &&
              selectedMember?.role !== "admin" &&
              showCalendarAssignment &&
              permissions.appointments && (
                <MemberCalendarAccessField
                  syncCalendars={syncCalendars}
                  selectedCalendarIds={selectedCalendarIds}
                  onChange={setSelectedCalendarIds}
                  profile={detectMemberProfile(permissions)}
                />
              )}

            {canConfigureChannelAccess &&
              selectedMember?.role !== "owner" &&
              permissions.conversations &&
              messagingIntegrations.length > 0 && (
                <MemberIntegrationAccessField
                  integrations={messagingIntegrations}
                  selectedIntegrationIds={selectedIntegrationIds}
                  onChange={setSelectedIntegrationIds}
                />
              )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateMember} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog de Confirmação de Exclusão */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="space-y-4">
            <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-amber-600 dark:text-amber-500" />
              </div>
              Remover Membro
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-sm">
                  <span className="text-muted-foreground">Remover: </span>
                  <span className="font-semibold text-primary">{selectedMember?.display_name || selectedMember?.email}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Esta ação irá remover permanentemente:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    Acesso do membro ao workspace
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    Todas as permissões configuradas
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="border-0 bg-transparent hover:bg-muted">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Trash2 className="w-4 h-4 mr-2" />
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
